import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import AnalyticsSetupPageAnalysisTypes from './AnalyticsSetupPageAnalysisTypes';
import AnalyticsSetupPagePromptTemplates from './AnalyticsSetupPagePromptTemplates';
import AnalyticsSetupPageScriptSetup from './AnalyticsSetupPageScriptSetup';
import './AnalyticsSetup.css';

const AGENT_API_BASE_URL = process.env.REACT_APP_AGENT_API_URL || '';
const ANALYTICS_API_URL = AGENT_API_BASE_URL ? `${AGENT_API_BASE_URL}/api` : '/api';

const buildDefaultNoneType = (type) => ({
  id: 'none',
  name: 'none',
  type,
  label: 'None',
  settings: {
    configurable: false
  }
});

const DEFAULT_MAIN_ANALYSIS_TYPES = {
  none: buildDefaultNoneType('main')
};

const DEFAULT_EXTRA_ANALYSIS_TYPES = {
  none: buildDefaultNoneType('extra')
};

const normalizeAnalysisTypeMap = (rawTypes, type) => {
  const normalizedMap = {
    none: buildDefaultNoneType(type)
  };

  if (!rawTypes || typeof rawTypes !== 'object') {
    return normalizedMap;
  }

  Object.values(rawTypes).forEach((item) => {
    const normalizedType = String(item?.type || type).trim().toLowerCase();
    if (normalizedType !== type) {
      return;
    }

    const normalizedName = String(item?.name || item?.id || '').trim().toLowerCase();
    if (!normalizedName || normalizedName === 'none') {
      return;
    }

    const label = String(item?.label || '').trim() || normalizedName;
    const configurable = !!item?.settings?.configurable;

    normalizedMap[normalizedName] = {
      id: normalizedName,
      name: normalizedName,
      type,
      label,
      settings: {
        configurable: !!configurable
      }
    };
  });

  return normalizedMap;
};

const ADVANCED_SETUP_TABS = [
  { id: 'script-setup', label: 'Script Setup' },
  { id: 'prompt-templates', label: 'Prompt templates' },
  { id: 'analysis-types', label: 'Analysis types' },
];
const ADVANCED_SETUP_TAB_IDS = new Set(ADVANCED_SETUP_TABS.map((tab) => tab.id));

const TOP_LEVEL_TABS = [
  { id: 'setup-wizard', label: 'Setup Wizard' },
  { id: 'advanced-setup', label: 'Advanced setup' },
];

const SETUP_WIZARD_STEPS = [
  { id: 'step-1', label: 'STEP 1 - CHOOSE SCRIPT & ANALYSIS TYPE' },
  { id: 'step-2', label: 'STEP 2 - PROMPT & QUESTIONS' },
  { id: 'step-3', label: 'STEP 3 - RUN & RESULTS' },
];

const AnalyticsSetup = ({ initialSelection, onSelectionApplied, allowedScriptId = null }) => {
  const [activeTopTab, setActiveTopTab] = useState('setup-wizard');
  const [activeAdvancedTab, setActiveAdvancedTab] = useState('script-setup');
  const [activeWizardStep, setActiveWizardStep] = useState(SETUP_WIZARD_STEPS[0].id);
  const [mainAnalysisTypes, setMainAnalysisTypes] = useState(DEFAULT_MAIN_ANALYSIS_TYPES);
  const [extraAnalysisTypes, setExtraAnalysisTypes] = useState(DEFAULT_EXTRA_ANALYSIS_TYPES);
  const [analysisTypeLoadError, setAnalysisTypeLoadError] = useState('');
  const [loadingAnalysisTypes, setLoadingAnalysisTypes] = useState(false);
  const [analysisTypesReady, setAnalysisTypesReady] = useState(false);
  const [analysisTypesVersion, setAnalysisTypesVersion] = useState(0);

  const fetchAnalysisTypeSettings = useCallback(async () => {
    const { data } = await api.get('/analysis-type-settings', { baseURL: ANALYTICS_API_URL });
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to load analysis type settings');
    }

    return {
      main: normalizeAnalysisTypeMap(data?.mainAnalysisTypes, 'main'),
      extra: normalizeAnalysisTypeMap(data?.extraAnalysisTypes, 'extra'),
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAnalysisTypeSettings = async () => {
      setLoadingAnalysisTypes(true);
      setAnalysisTypeLoadError('');

      try {
        const settings = await fetchAnalysisTypeSettings();

        if (!isMounted) return;

        setMainAnalysisTypes(settings.main);
        setExtraAnalysisTypes(settings.extra);
      } catch (error) {
        if (!isMounted) return;

        setMainAnalysisTypes(DEFAULT_MAIN_ANALYSIS_TYPES);
        setExtraAnalysisTypes(DEFAULT_EXTRA_ANALYSIS_TYPES);
        setAnalysisTypeLoadError(error?.message || 'Failed to load analysis type settings');
      } finally {
        if (isMounted) {
          setLoadingAnalysisTypes(false);
          setAnalysisTypesReady(true);
        }
      }
    };

    loadAnalysisTypeSettings();

    return () => {
      isMounted = false;
    };
  }, [fetchAnalysisTypeSettings]);

  const handleTypesChanged = useCallback(async () => {
    setLoadingAnalysisTypes(true);
    setAnalysisTypeLoadError('');

    try {
      const settings = await fetchAnalysisTypeSettings();
      setMainAnalysisTypes(settings.main);
      setExtraAnalysisTypes(settings.extra);
      setAnalysisTypeLoadError('');
      setAnalysisTypesVersion((prev) => prev + 1);
    } catch (error) {
      setMainAnalysisTypes(DEFAULT_MAIN_ANALYSIS_TYPES);
      setExtraAnalysisTypes(DEFAULT_EXTRA_ANALYSIS_TYPES);
      setAnalysisTypeLoadError(error?.message || 'Failed to load analysis type settings');
    } finally {
      setLoadingAnalysisTypes(false);
      setAnalysisTypesReady(true);
    }
  }, [fetchAnalysisTypeSettings]);

  useEffect(() => {
    const requestedTab = String(initialSelection?.tab || '').trim();
    if (requestedTab && ADVANCED_SETUP_TAB_IDS.has(requestedTab)) {
      setActiveTopTab('advanced-setup');
      setActiveAdvancedTab(requestedTab);
    }
  }, [initialSelection]);

  const currentWizardStepIndex = SETUP_WIZARD_STEPS.findIndex((step) => step.id === activeWizardStep);
  const hasPreviousWizardStep = currentWizardStepIndex > 0;
  const hasNextWizardStep = currentWizardStepIndex >= 0 && currentWizardStepIndex < SETUP_WIZARD_STEPS.length - 1;

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Analytics Setup</h1>
            <p className="text-sm text-gray-500">
              Configure script analysis type and analysis prompt templates.
            </p>
          </div>
        </header>

        <nav className="border-b border-gray-200">
          <div className="flex flex-wrap gap-1">
            {TOP_LEVEL_TABS.map((tab) => {
              const isActive = activeTopTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTopTab(tab.id)}
                  className={`rounded-t-lg border border-b-0 px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? 'border-gray-200 bg-white text-gray-900'
                      : 'border-transparent bg-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  aria-pressed={isActive}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </nav>

        {activeTopTab === 'setup-wizard' && (
          <section className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <nav className="flex flex-wrap items-center" aria-label="Setup wizard steps">
                {SETUP_WIZARD_STEPS.map((step, index) => {
                  const stepNumber = index + 1;
                  const isActive = activeWizardStep === step.id;
                  const isPast = currentWizardStepIndex > index;

                  return (
                    <React.Fragment key={step.id}>
                      <button
                        type="button"
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition ${
                          isActive
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-gray-100'
                        }`}
                        onClick={() => setActiveWizardStep(step.id)}
                        aria-current={isActive ? 'step' : undefined}
                      >
                        <span
                          className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                            isActive
                              ? 'bg-teal-600 text-white'
                              : isPast
                                ? 'bg-teal-300 text-white'
                                : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {stepNumber}
                        </span>
                        <span className="text-sm font-semibold">{step.label}</span>
                      </button>
                      {index < SETUP_WIZARD_STEPS.length - 1 && (
                        <span className="mx-1 h-[2px] w-6 flex-shrink-0 bg-gray-200" aria-hidden="true" />
                      )}
                    </React.Fragment>
                  );
                })}
              </nav>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => setActiveWizardStep(SETUP_WIZARD_STEPS[currentWizardStepIndex - 1].id)}
                  disabled={!hasPreviousWizardStep}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => setActiveWizardStep(SETUP_WIZARD_STEPS[currentWizardStepIndex + 1].id)}
                  disabled={!hasNextWizardStep}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="min-h-[420px] rounded-xl border border-gray-200 bg-white p-6">
              <div className="space-y-4">
                {(!analysisTypesReady || loadingAnalysisTypes || analysisTypeLoadError) && (
                  <div className={analysisTypeLoadError ? 'text-sm text-amber-600' : 'text-sm text-gray-500'}>
                    {analysisTypeLoadError
                      ? `${analysisTypeLoadError}. Showing default "none" type only.`
                      : 'Loading analysis type settings...'}
                  </div>
                )}

                {analysisTypesReady && (
                  <AnalyticsSetupPageScriptSetup
                    key={`analytics-wizard-script-setup-${analysisTypesVersion}`}
                    initialSelection={initialSelection}
                    onSelectionApplied={onSelectionApplied}
                    mainAnalysisTypes={mainAnalysisTypes}
                    extraAnalysisTypes={extraAnalysisTypes}
                    wizardStep={activeWizardStep}
                    showGenerateButton={false}
                    showPurgeAnalysisLogsButton={false}
                    allowedScriptId={allowedScriptId}
                  />
                )}
              </div>

            </div>
          </section>
        )}

        {activeTopTab === 'advanced-setup' && (
          <>
            <nav className="border-b border-gray-200">
              <div className="flex flex-wrap gap-1">
                {ADVANCED_SETUP_TABS.map((tab) => {
                  const isActive = activeAdvancedTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveAdvancedTab(tab.id)}
                      className={`rounded-t-lg border border-b-0 px-4 py-2 text-sm font-semibold transition ${
                        isActive
                          ? 'border-gray-200 bg-white text-gray-900'
                          : 'border-transparent bg-transparent text-gray-500 hover:text-gray-700'
                      }`}
                      aria-pressed={isActive}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </nav>

            {(!analysisTypesReady || loadingAnalysisTypes || analysisTypeLoadError) && (
              <div className={analysisTypeLoadError ? 'text-sm text-amber-600' : 'text-sm text-gray-500'}>
                {analysisTypeLoadError
                  ? `${analysisTypeLoadError}. Showing default "none" type only.`
                  : 'Loading analysis type settings...'}
              </div>
            )}

            {analysisTypesReady && (
              <>
                <div className={activeAdvancedTab === 'script-setup' ? 'block' : 'hidden'} aria-hidden={activeAdvancedTab !== 'script-setup'}>
                  <AnalyticsSetupPageScriptSetup
                    key={`analytics-script-setup-${analysisTypesVersion}`}
                    initialSelection={initialSelection}
                    onSelectionApplied={onSelectionApplied}
                    mainAnalysisTypes={mainAnalysisTypes}
                    extraAnalysisTypes={extraAnalysisTypes}
                    allowedScriptId={allowedScriptId}
                  />
                </div>

                <div className={activeAdvancedTab === 'prompt-templates' ? 'block' : 'hidden'} aria-hidden={activeAdvancedTab !== 'prompt-templates'}>
                  <AnalyticsSetupPagePromptTemplates
                    key={`analytics-prompt-templates-${analysisTypesVersion}`}
                    mainAnalysisTypes={mainAnalysisTypes}
                    extraAnalysisTypes={extraAnalysisTypes}
                  />
                </div>

                <div className={activeAdvancedTab === 'analysis-types' ? 'block' : 'hidden'} aria-hidden={activeAdvancedTab !== 'analysis-types'}>
                  <AnalyticsSetupPageAnalysisTypes onTypesChanged={handleTypesChanged} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsSetup;
